import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { KeyCompositionProfile, KeyCompositionRow } from '../../services/api/keyCompositionService';
import {
  fetchKeyCompositionList,
  resolveKeyComposition,
  createKeyComposition,
  supersedeKeyComposition,
  deleteKeyComposition,
} from '../thunks/keyCompositionThunks';

export interface KeyCompositionFilters {
  scopePathPrefix: string;
  scopeLevel: number | null;     // null = any
  keyType: string;               // '' = any
  mixKind: string;               // '' = any
  includeExpired: boolean;
}

export interface KeyCompositionState {
  list: {
    items: KeyCompositionRow[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    error: string | null;
  };
  /** Cache keyed by `keyCompositionCacheKey()`. `null` = fetched + missing. */
  resolved: Record<string, KeyCompositionProfile | null>;
  filters: KeyCompositionFilters;
}

const initialState: KeyCompositionState = {
  list: {
    items: [],
    pageIndex: 1,
    pageSize: 50,
    totalCount: 0,
    isLoading: false,
    error: null,
  },
  resolved: {},
  filters: {
    scopePathPrefix: '',
    scopeLevel: null,
    keyType: '',
    mixKind: '',
    includeExpired: false,
  },
};

const keyCompositionSlice = createSlice({
  name: 'keyComposition',
  initialState,
  reducers: {
    clearError: (state) => { state.list.error = null; },
    setFilter: <K extends keyof KeyCompositionFilters>(
      state: KeyCompositionState,
      action: PayloadAction<{ key: K; value: KeyCompositionFilters[K] }>
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    clearFilters: (state) => { state.filters = { ...initialState.filters }; },
    invalidateResolved: (state) => { state.resolved = {}; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKeyCompositionList.pending, (state) => {
        state.list.isLoading = true;
        state.list.error = null;
      })
      .addCase(fetchKeyCompositionList.fulfilled, (state, action) => {
        state.list.isLoading = false;
        state.list.items = action.payload.items;
        state.list.pageIndex = action.payload.pageIndex;
        state.list.pageSize = action.payload.pageSize;
        state.list.totalCount = action.payload.totalCount;
      })
      .addCase(fetchKeyCompositionList.rejected, (state, action) => {
        state.list.isLoading = false;
        state.list.error = action.payload as string;
      });

    builder
      .addCase(resolveKeyComposition.fulfilled, (state, action) => {
        state.resolved[action.payload.key] = action.payload.data;
      });

    // Successful writes invalidate the resolved cache so consumers refetch.
    builder
      .addCase(createKeyComposition.fulfilled, (state) => { state.resolved = {}; })
      .addCase(supersedeKeyComposition.fulfilled, (state) => { state.resolved = {}; })
      .addCase(deleteKeyComposition.fulfilled, (state, action) => {
        state.resolved = {};
        state.list.items = state.list.items.filter((x) => x.id !== action.payload);
        state.list.totalCount = Math.max(0, state.list.totalCount - 1);
      });
  },
});

export const { clearError, setFilter, clearFilters, invalidateResolved } = keyCompositionSlice.actions;
export default keyCompositionSlice.reducer;
